/**
 * Expansion Simulation Mode for CognitiveOS Structure Page
 * Allows executives to simulate hiring and project expansion scenarios
 */
import React, { useState } from 'react';
import { analyticsAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Progress } from '../../components/ui/progress';
import { 
  Play, AlertTriangle, CheckCircle2, TrendingUp, Users, 
  Briefcase, Brain, Target, RefreshCw, ArrowRight
} from 'lucide-react';

// Cognitive Lanes for selection
const COGNITIVE_LANES = [
  { id: 'vision', name: 'Vision', color: '#8B5CF6' },
  { id: 'strategy', name: 'Strategy', color: '#6366F1' },
  { id: 'creative', name: 'Creative', color: '#EC4899' },
  { id: 'marketing', name: 'Marketing', color: '#F59E0B' },
  { id: 'revenue', name: 'Revenue', color: '#10B981' },
  { id: 'relationships', name: 'Relationships', color: '#06B6D4' },
  { id: 'culture', name: 'Culture', color: '#8B5CF6' },
  { id: 'operations', name: 'Operations', color: '#3B82F6' },
  { id: 'execution', name: 'Execution', color: '#64748B' },
  { id: 'finance', name: 'Finance', color: '#14B8A6' }
];

// Risk forecast configuration
const getRiskConfig = (forecast) => {
  switch (forecast) {
    case 'low': return { color: '#10B981', bg: '#D1FAE5', label: 'Low Risk' };
    case 'moderate': return { color: '#F59E0B', bg: '#FEF3C7', label: 'Moderate Risk' };
    case 'high': return { color: '#EF4444', bg: '#FEE2E2', label: 'High Risk' };
    default: return { color: '#6B7280', bg: '#F3F4F6', label: 'Unknown' };
  }
};

const ExpansionSimulatorTab = () => {
  const [regionName, setRegionName] = useState('New Region');
  const [targetHeadcount, setTargetHeadcount] = useState(10);
  const [expectedProjectLoad, setExpectedProjectLoad] = useState(5);
  const [selectedLanes, setSelectedLanes] = useState(['execution', 'operations']);
  const [simulationResult, setSimulationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const toggleLane = (laneId) => {
    setSelectedLanes(prev => 
      prev.includes(laneId) 
        ? prev.filter(l => l !== laneId)
        : [...prev, laneId]
    );
  };

  const runSimulation = async () => {
    if (selectedLanes.length === 0) {
      setError('Please select at least one required lane');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await analyticsAPI.simulateExpansion({
        new_region_name: regionName,
        target_headcount: parseInt(targetHeadcount),
        expected_project_load: parseInt(expectedProjectLoad),
        required_lanes: selectedLanes
      });
      setSimulationResult(response.data);
    } catch (err) {
      console.error('Simulation error:', err);
      setError('Failed to run simulation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetSimulation = () => {
    setSimulationResult(null);
    setError(null);
    setRegionName('New Region');
    setTargetHeadcount(10);
    setExpectedProjectLoad(5);
    setSelectedLanes(['execution', 'operations']);
  };

  const riskConfig = simulationResult ? getRiskConfig(simulationResult.risk_forecast) : null;

  return (
    <div className="space-y-6" data-testid="expansion-simulator">
      {/* Header */}
      <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-xl p-6 text-white">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
            <TrendingUp className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Expansion Simulation Mode</h2>
            <p className="text-sm text-white/80">Model the impact of growth scenarios on your organization</p>
          </div>
        </div>
        <p className="text-sm text-white/70">
          Test different hiring and project expansion scenarios to identify structural gaps, 
          predict overload risks, and plan proactive adjustments before scaling.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Panel */}
        <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E9ECEF' }}>
          <h3 className="text-base font-semibold mb-4" style={{ color: '#264653' }}>Simulation Parameters</h3>
          
          {/* Region Name */}
          <div className="mb-5">
            <label className="text-sm font-medium mb-2 block" style={{ color: '#374151' }}>
              Expansion Region/Division Name
            </label>
            <Input
              type="text"
              value={regionName}
              onChange={(e) => setRegionName(e.target.value)}
              placeholder="e.g., APAC Division, New York Office"
              className="w-full"
              data-testid="region-name-input"
            />
          </div>

          {/* Target Headcount */}
          <div className="mb-5">
            <label className="text-sm font-medium mb-2 block" style={{ color: '#374151' }}>
              Target Headcount Increase
            </label>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                value={targetHeadcount}
                onChange={(e) => setTargetHeadcount(e.target.value)}
                min={1}
                max={100}
                className="w-24"
                data-testid="headcount-input"
              />
              <span className="text-sm" style={{ color: '#6B7280' }}>new hires planned</span>
            </div>
          </div>

          {/* Expected Project Load */}
          <div className="mb-5">
            <label className="text-sm font-medium mb-2 block" style={{ color: '#374151' }}>
              Expected New Project Load
            </label>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                value={expectedProjectLoad}
                onChange={(e) => setExpectedProjectLoad(e.target.value)}
                min={1}
                max={50}
                className="w-24"
                data-testid="project-load-input"
              />
              <span className="text-sm" style={{ color: '#6B7280' }}>additional projects</span>
            </div>
          </div>

          {/* Required Lanes */}
          <div className="mb-6">
            <label className="text-sm font-medium mb-3 block" style={{ color: '#374151' }}>
              Required Lane Capabilities
            </label>
            <div className="flex flex-wrap gap-2">
              {COGNITIVE_LANES.map((lane) => {
                const isSelected = selectedLanes.includes(lane.id);
                return (
                  <button
                    key={lane.id}
                    onClick={() => toggleLane(lane.id)}
                    className="px-3 py-1.5 rounded-full text-sm font-medium transition-all"
                    style={{
                      backgroundColor: isSelected ? lane.color : '#F3F4F6',
                      color: isSelected ? 'white' : '#6B7280',
                      border: isSelected ? `2px solid ${lane.color}` : '2px solid transparent'
                    }}
                    data-testid={`lane-${lane.id}`}
                  >
                    {lane.name}
                  </button>
                );
              })}
            </div>
            <p className="text-xs mt-2" style={{ color: '#9CA3AF' }}>
              Select lanes required for your expansion plans
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 rounded-lg mb-4" style={{ backgroundColor: '#FEE2E2' }}>
              <p className="text-sm" style={{ color: '#DC2626' }}>{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={runSimulation}
              disabled={loading}
              className="gap-2"
              style={{ backgroundColor: '#6366F1' }}
              data-testid="run-simulation-btn"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              {loading ? 'Simulating...' : 'Run Simulation'}
            </Button>
            {simulationResult && (
              <Button variant="outline" onClick={resetSimulation} className="gap-2">
                <RefreshCw className="w-4 h-4" />
                Reset
              </Button>
            )}
          </div>
        </div>

        {/* Results Panel */}
        <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E9ECEF' }}>
          <h3 className="text-base font-semibold mb-4" style={{ color: '#264653' }}>Simulation Results</h3>
          
          {!simulationResult ? (
            <div className="text-center py-12" style={{ color: '#9CA3AF' }}>
              <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">Configure parameters and run a simulation</p>
              <p className="text-xs mt-1">Results will appear here</p>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Viability Score */}
              <div className="p-4 rounded-lg" style={{ backgroundColor: riskConfig.bg }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium" style={{ color: riskConfig.color }}>
                    Expansion Viability
                  </span>
                  <span 
                    className="px-2 py-0.5 rounded-full text-xs font-medium"
                    style={{ backgroundColor: riskConfig.color, color: 'white' }}
                  >
                    {riskConfig.label}
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold" style={{ color: riskConfig.color }}>
                    {simulationResult.viability_score}
                  </span>
                  <span className="text-sm" style={{ color: '#6B7280' }}>/100</span>
                </div>
                <Progress 
                  value={simulationResult.viability_score} 
                  className="h-2 mt-2" 
                />
              </div>

              {/* Key Metrics Grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg" style={{ backgroundColor: '#F9FAFB' }}>
                  <div className="flex items-center gap-2 mb-1">
                    <Brain className="w-4 h-4" style={{ color: '#6B7280' }} />
                    <span className="text-xs" style={{ color: '#6B7280' }}>Decision Strain</span>
                  </div>
                  <p 
                    className="text-xl font-semibold"
                    style={{ color: simulationResult.decision_capacity_strain > 60 ? '#EF4444' : '#264653' }}
                  >
                    {simulationResult.decision_capacity_strain}%
                  </p>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: '#F9FAFB' }}>
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4" style={{ color: '#6B7280' }} />
                    <span className="text-xs" style={{ color: '#6B7280' }}>Overload Impact</span>
                  </div>
                  <p 
                    className="text-xl font-semibold"
                    style={{ color: simulationResult.predicted_overload_impact > 30 ? '#F59E0B' : '#264653' }}
                  >
                    {simulationResult.predicted_overload_impact}%
                  </p>
                </div>
              </div>

              {/* Required Hires by Lane */}
              {Object.keys(simulationResult.required_hires_by_lane || {}).length > 0 && (
                <div>
                  <h4 className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: '#6B7280' }}>
                    Required Hires by Lane
                  </h4>
                  <div className="space-y-2">
                    {Object.entries(simulationResult.required_hires_by_lane).map(([lane, count]) => {
                      const laneInfo = COGNITIVE_LANES.find(l => l.id === lane);
                      return (
                        <div key={lane} className="flex items-center justify-between p-2 rounded" style={{ backgroundColor: '#F9FAFB' }}>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: laneInfo?.color || '#6B7280' }} />
                            <span className="text-sm capitalize" style={{ color: '#374151' }}>{lane}</span>
                          </div>
                          <span className="text-sm font-medium" style={{ color: '#264653' }}>+{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Level Distribution */}
              {simulationResult.required_level_distribution && (
                <div>
                  <h4 className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: '#6B7280' }}>
                    Recommended Level Distribution
                  </h4>
                  <div className="flex gap-2">
                    {Object.entries(simulationResult.required_level_distribution).map(([level, count]) => (
                      <div key={level} className="flex-1 p-2 rounded text-center" style={{ backgroundColor: '#F9FAFB' }}>
                        <p className="text-lg font-semibold" style={{ color: '#264653' }}>{count}</p>
                        <p className="text-xs" style={{ color: '#6B7280' }}>{level}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Structural Gaps */}
              {simulationResult.structural_gaps_created?.length > 0 && (
                <div className="p-3 rounded-lg" style={{ backgroundColor: '#FEF3C7' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4" style={{ color: '#D97706' }} />
                    <span className="text-sm font-medium" style={{ color: '#92400E' }}>Structural Gaps</span>
                  </div>
                  <ul className="space-y-1">
                    {simulationResult.structural_gaps_created.map((gap, idx) => (
                      <li key={idx} className="text-xs" style={{ color: '#78350F' }}>• {gap}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Pre-Expansion Adjustments */}
              <div className="border-t pt-4" style={{ borderColor: '#E9ECEF' }}>
                <h4 className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: '#6B7280' }}>
                  Recommended Pre-Expansion Actions
                </h4>
                <div className="space-y-2">
                  {simulationResult.pre_expansion_adjustments?.map((action, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <ArrowRight className="w-3 h-3 mt-1 flex-shrink-0" style={{ color: '#6366F1' }} />
                      <p className="text-sm" style={{ color: '#374151' }}>{action}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpansionSimulatorTab;
